/**
 * In-editor find & replace (`061-editor.md` — editor features; `110-design-system.md` §7).
 *
 * A **custom, in-repo** extension, deliberately — not `@tiptap/extension-search-and-replace`
 * / `prosemirror-search`, which are not in `020-tech-stack.md` (an ADR, not an `npm install`
 * — see the header of `extensions.ts`). It follows the same pattern as `wikiLink.ts`: a Tiptap
 * `Extension` that owns one ProseMirror plugin and a set of commands.
 *
 * **It adds no node and no mark.** Matches are painted with view-only `Decoration`s, which
 * never enter `page.content`; the schema is unchanged, so this extension can live in the shared
 * `extensions` list without touching the paste-fixture `getSchema(extensions)` corpus, and it
 * cannot widen the XSS surface (`120-non-functional.md`).
 *
 * Replace edits are ordinary transactions, so they flow through Tiptap's `onUpdate` → autosave
 * → outbox like any other edit; the caller persists a replace-all immediately with `saveNow`.
 */
import { Extension, type Editor } from '@tiptap/core';
import {
  Plugin,
  PluginKey,
  TextSelection,
  type EditorState,
  type Transaction,
} from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';
import type { Node as ProseMirrorNode } from '@tiptap/pm/model';

/** A match, as absolute document positions: `[from, to)`. */
export interface SearchMatch {
  from: number;
  to: number;
}

interface SearchState {
  query: string;
  caseSensitive: boolean;
  matches: SearchMatch[];
  /** Index into `matches` of the active match, or `-1` when there are none. */
  current: number;
  decorations: DecorationSet;
}

export const searchPluginKey = new PluginKey<SearchState>('rpnSearch');

/**
 * All matches of `query` in `doc`, as document positions.
 *
 * Pure and exported so the position mapping is a unit test, not a bug report (`061` §4's
 * "pure, unit-tested function" ethos). Matching is confined to **within a single textblock**:
 * a search never spans a paragraph/heading/list-item boundary, which is what a reader means by
 * "find this phrase". Inline atoms (a wiki link, an inline image) are represented by a sentinel
 * that no ordinary query contains, so a match can never run through one either.
 */
export function findMatches(
  doc: ProseMirrorNode,
  query: string,
  caseSensitive: boolean,
): SearchMatch[] {
  const matches: SearchMatch[] = [];
  if (query === '') return matches;

  const needle = caseSensitive ? query : query.toLowerCase();

  doc.descendants((node, pos) => {
    if (!node.isTextblock) return true; // a container — descend into it

    // Build this block's text and a position map: `positions[i]` is the document position of
    // the i-th character. A text child contributes its characters; any other inline node (an
    // atom) contributes one sentinel char occupying its single position, keeping the map aligned.
    let text = '';
    const positions: number[] = [];

    node.forEach((child, offset) => {
      const start = pos + 1 + offset; // +1 to step inside the textblock
      if (child.isText) {
        const str = child.text ?? '';
        for (let i = 0; i < str.length; i++) {
          text += str[i];
          positions.push(start + i);
        }
      } else {
        text += '￿'; // sentinel: never part of a user query
        positions.push(start);
      }
    });

    const haystack = caseSensitive ? text : text.toLowerCase();
    let idx = haystack.indexOf(needle);
    while (idx !== -1) {
      const from = positions[idx];
      const lastCharPos = positions[idx + needle.length - 1];
      if (from !== undefined && lastCharPos !== undefined) {
        matches.push({ from, to: lastCharPos + 1 });
      }
      // Non-overlapping, left-to-right — the same as a browser's find.
      idx = haystack.indexOf(needle, idx + needle.length);
    }

    return false; // a textblock's children are inline; we have handled them all
  });

  return matches;
}

/** Keep `current` pointing at a real match after the match set changes. */
function clampCurrent(current: number, count: number): number {
  if (count === 0) return -1;
  if (current < 0) return 0;
  if (current >= count) return count - 1;
  return current;
}

function buildDecorations(
  doc: ProseMirrorNode,
  matches: SearchMatch[],
  current: number,
): DecorationSet {
  if (matches.length === 0) return DecorationSet.empty;

  const decorations = matches.map((match, index) =>
    Decoration.inline(match.from, match.to, {
      class: index === current ? 'search-match search-match-current' : 'search-match',
    }),
  );

  return DecorationSet.create(doc, decorations);
}

/**
 * Scroll the active match into view.
 *
 * A transaction's `scrollIntoView()` cannot do this job here: `EditorView.scrollToSelection`
 * silently no-ops when the DOM selection sits outside the editor, and this panel keeps focus in
 * the find field by design — so in a document long enough to scroll, nothing moved. Scrolling the
 * active decoration's own element also centres the match, clearing the floating panel, which
 * ProseMirror's 5px `scrollMargin` would not.
 *
 * The `requestAnimationFrame` matters: the decoration for the new `current` only reaches the DOM
 * after the view has applied the dispatched transaction.
 */
export function scrollCurrentMatchIntoView(editor: Editor): void {
  requestAnimationFrame(() => {
    editor.view.dom
      .querySelector('.search-match-current')
      ?.scrollIntoView({ block: 'center', behavior: 'auto' });
  });
}

/** The partial state a command pushes through a transaction's meta. */
type SearchMeta = Partial<Pick<SearchState, 'query' | 'caseSensitive' | 'current'>>;

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    rpnSearch: {
      /** Set the query and jump to the first match. */
      setSearchTerm: (query: string) => ReturnType;
      /** Toggle case sensitivity, re-running the current query. */
      setSearchCaseSensitive: (caseSensitive: boolean) => ReturnType;
      /** Select the next match, wrapping past the end. */
      findNext: () => ReturnType;
      /** Select the previous match, wrapping past the start. */
      findPrevious: () => ReturnType;
      /** Replace the active match with `replacement`. */
      replaceCurrent: (replacement: string) => ReturnType;
      /** Replace every match with `replacement`, in one undoable step. */
      replaceAll: (replacement: string) => ReturnType;
      /** Clear the query and remove all match highlights. */
      clearSearch: () => ReturnType;
    };
  }
}

export const RpnSearch = Extension.create({
  name: 'rpnSearch',

  addProseMirrorPlugins() {
    return [
      new Plugin<SearchState>({
        key: searchPluginKey,

        state: {
          init: () => ({
            query: '',
            caseSensitive: false,
            matches: [],
            current: -1,
            decorations: DecorationSet.empty,
          }),

          apply(tr, value): SearchState {
            const meta = tr.getMeta(searchPluginKey) as SearchMeta | undefined;
            let next: SearchState = meta ? { ...value, ...meta } : value;

            // Recompute matches when the query/case changed, or when the document changed under
            // an active query (so highlights track live edits and replaces).
            const queryOrCaseChanged =
              meta !== undefined && ('query' in meta || 'caseSensitive' in meta);
            const docChangedUnderQuery = tr.docChanged && next.query !== '';

            if (queryOrCaseChanged || docChangedUnderQuery) {
              const matches =
                next.query === '' ? [] : findMatches(tr.doc, next.query, next.caseSensitive);
              next = { ...next, matches, current: clampCurrent(next.current, matches.length) };
            }

            // Rebuild decorations whenever anything relevant moved (query, doc, or the active
            // match index via findNext/findPrevious).
            if (meta !== undefined || tr.docChanged) {
              next = {
                ...next,
                decorations: buildDecorations(tr.doc, next.matches, next.current),
              };
            }

            return next;
          },
        },

        props: {
          decorations(state) {
            return searchPluginKey.getState(state)?.decorations ?? DecorationSet.empty;
          },
        },
      }),
    ];
  },

  addCommands() {
    /** Move the active match and scroll it into view without stealing focus from the panel. */
    const goToMatch =
      (index: number) =>
      ({
        state,
        tr,
        dispatch,
      }: {
        state: EditorState;
        tr: Transaction;
        dispatch: ((tr: Transaction) => void) | undefined;
      }): boolean => {
        const search = searchPluginKey.getState(state);
        if (search === undefined || search.matches.length === 0) return false;
        const match = search.matches[index];
        if (match === undefined) return false;
        if (dispatch) {
          tr.setSelection(TextSelection.create(tr.doc, match.from, match.to))
            .setMeta(searchPluginKey, { current: index } satisfies SearchMeta)
            .scrollIntoView();
          dispatch(tr);
        }
        return true;
      };

    return {
      setSearchTerm:
        (query) =>
        ({ tr, dispatch }) => {
          dispatch?.(tr.setMeta(searchPluginKey, { query, current: 0 } satisfies SearchMeta));
          return true;
        },

      setSearchCaseSensitive:
        (caseSensitive) =>
        ({ tr, dispatch }) => {
          dispatch?.(
            tr.setMeta(searchPluginKey, { caseSensitive, current: 0 } satisfies SearchMeta),
          );
          return true;
        },

      findNext:
        () =>
        ({ state, tr, dispatch }) => {
          const search = searchPluginKey.getState(state);
          if (search === undefined || search.matches.length === 0) return false;
          const index = (search.current + 1) % search.matches.length;
          return goToMatch(index)({ state, tr, dispatch });
        },

      findPrevious:
        () =>
        ({ state, tr, dispatch }) => {
          const search = searchPluginKey.getState(state);
          if (search === undefined || search.matches.length === 0) return false;
          const count = search.matches.length;
          const index = (search.current - 1 + count) % count;
          return goToMatch(index)({ state, tr, dispatch });
        },

      replaceCurrent:
        (replacement) =>
        ({ state, tr, dispatch }) => {
          const search = searchPluginKey.getState(state);
          if (search === undefined || search.current < 0) return false;
          const match = search.matches[search.current];
          if (match === undefined) return false;
          // The rematch on `docChanged` keeps `current` at this index, which now lands on the
          // following match — so repeated Replace walks forward, as a user expects.
          if (dispatch) dispatch(tr.insertText(replacement, match.from, match.to));
          return true;
        },

      replaceAll:
        (replacement) =>
        ({ state, tr, dispatch }) => {
          const search = searchPluginKey.getState(state);
          if (search === undefined || search.matches.length === 0) return false;
          if (dispatch) {
            // Back-to-front, so each replacement leaves the positions of the earlier matches
            // untouched. One transaction → one undo step (`Mod+Z` reverts the whole replace).
            for (let i = search.matches.length - 1; i >= 0; i--) {
              const match = search.matches[i];
              if (match !== undefined) tr.insertText(replacement, match.from, match.to);
            }
            dispatch(tr);
          }
          return true;
        },

      clearSearch:
        () =>
        ({ tr, dispatch }) => {
          dispatch?.(tr.setMeta(searchPluginKey, { query: '', current: -1 } satisfies SearchMeta));
          return true;
        },
    };
  },
});
